import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Chip,
    Tooltip,
    TextField,
    Button,
    Menu,
    MenuItem,
    MenuList,
    ListItemText,
    Divider,
    Snackbar,
    Alert,
    Autocomplete,
    Avatar,
    CircularProgress,
    Popper,
    Paper,
    ClickAwayListener,
    Dialog,
    DialogContent,
} from '@mui/material';
import {
    Close as CloseIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Delete as DeleteIcon,
    Link as LinkIcon,
    BugReport as BugIcon,
    Send as SendIcon,
    Image as ImageIcon,
    History as HistoryIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import IssueContentRenderer from './IssueContentRenderer';
import RichTextEditor from './RichTextEditor';
import SlackMessageDialog from './SlackMessageDialog';
import CreateJiraBugDialog from './CreateJiraBugDialog';
import AuthenticatedImage from './AuthenticatedImage';
import { issueService } from '../services/issueService';
import api from '../services/api';
import { formatUtcToLocal } from '../utils/dateUtils';

/* ================================================================ */
/*  Colour helpers – same palette used by the IssueList table chips */
/* ================================================================ */
const statusColors = {
    Open:          { bgcolor: '#1976d2', color: '#fff' },
    'In Development': { bgcolor: '#ed6c02', color: '#fff' },
    Resolved:      { bgcolor: '#2e7d32', color: '#fff' },
    'Fix Deployed': { bgcolor: '#0288d1', color: '#fff' },
    'Testing in progress': { bgcolor: '#7b1fa2', color: '#fff' },
    'Fix not working': { bgcolor: '#d32f2f', color: '#fff' },
    Closed:        { bgcolor: '#546e7a', color: '#fff' },
    Backlog:       { bgcolor: '#78909c', color: '#fff' },
    Defaulter:     { bgcolor: '#880e4f', color: '#fff' },
};

const priorityColors = {
    Critical: { bgcolor: '#d32f2f', color: '#fff' },
    High:     { bgcolor: '#e65100', color: '#fff' },
    Medium:   { bgcolor: '#f57c00', color: '#fff' },
    Low:      { bgcolor: '#388e3c', color: '#fff' },
};

const severityColors = {
    Blocker:  { bgcolor: '#b71c1c', color: '#fff' },
    Critical: { bgcolor: '#c62828', color: '#fff' },
    Major:    { bgcolor: '#e65100', color: '#fff' },
    Minor:    { bgcolor: '#558b2f', color: '#fff' },
    Trivial:  { bgcolor: '#78909c', color: '#fff' },
};

/* ================================================================ */
/*  IssueDetailPanel                                                */
/* ================================================================ */
const IssueDetailPanel = ({
    open,
    onClose,
    issue,
    onIssueUpdated,          // callback: () => void  – refresh list
    modules = [],
    subModules = [],
    features = [],
    releases = [],
    jiraUsers = [],
    jiraUsersMap = {},
    jiraConnected = false,
}) => {
    /* ---------------------------------------------------- */
    /*  Local state mirrors of editable fields              */
    /* ---------------------------------------------------- */
    const [title, setTitle] = useState('');
    const [isEditingTitle, setIsEditingTitle] = useState(false);

    const [status, setStatus] = useState('');
    const [statusAnchor, setStatusAnchor] = useState(null);

    const [priority, setPriority] = useState('');
    const [priorityAnchor, setPriorityAnchor] = useState(null);

    const [severity, setSeverity] = useState('');
    const [severityAnchor, setSeverityAnchor] = useState(null);

    const [moduleId, setModuleId] = useState('');
    const [moduleAnchor, setModuleAnchor] = useState(null);

    const [subModuleId, setSubModuleId] = useState('');
    const [subModuleAnchor, setSubModuleAnchor] = useState(null);

    const [featureId, setFeatureId] = useState('');
    const [featureAnchor, setFeatureAnchor] = useState(null);

    const [releaseId, setReleaseId] = useState('');
    const [releaseAnchor, setReleaseAnchor] = useState(null);

    const [assigneeId, setAssigneeId] = useState('');
    const [assigneeAnchor, setAssigneeAnchor] = useState(null);

    const [localBugId, setLocalBugId] = useState('');

    /* Description editing */
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [editedDescription, setEditedDescription] = useState('');
    const [editedMediaFiles, setEditedMediaFiles] = useState([]);
    const [isSavingDescription, setIsSavingDescription] = useState(false);
    const [preloadedMediaUrls, setPreloadedMediaUrls] = useState([]);
    
    /* Local media state - used to ensure fresh URLs are used after save */
    const [localVideoUrl, setLocalVideoUrl] = useState(null);
    const [localScreenshotUrls, setLocalScreenshotUrls] = useState(null);

    /* Dialogs */
    const [slackDialogOpen, setSlackDialogOpen] = useState(false);
    const [createBugDialogOpen, setCreateBugDialogOpen] = useState(false);
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [expandedCommentImage, setExpandedCommentImage] = useState(null);
    const [stories, setStories] = useState([]);
    const [selectedStory, setSelectedStory] = useState(null);

    /* Snackbar */
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    /* Saving state (for individual patch calls) */
    const [updating, setUpdating] = useState(false);

    /* ---------------------------------------------------- */
    /*  Comments state                                      */
    /* ---------------------------------------------------- */
    const [comments, setComments] = useState([]);
    const [commentText, setCommentText] = useState('');
    const [postingComment, setPostingComment] = useState(false);
    const [slackUsers, setSlackUsers] = useState([]);
    const [mentionAnchor, setMentionAnchor] = useState(null);
    const [mentionFilter, setMentionFilter] = useState('');
    const [pendingMentions, setPendingMentions] = useState([]);
    const commentInputRef = useRef(null);
    const commentsEndRef = useRef(null);
    const commentImageInputRef = useRef(null);
    const [commentImageFile, setCommentImageFile] = useState(null);
    const [commentImagePreview, setCommentImagePreview] = useState(null);

    /* ---------------------------------------------------- */
    /*  Audit History state                                 */
    /* ---------------------------------------------------- */
    const [showHistory, setShowHistory] = useState(false);
    const [auditHistory, setAuditHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    /* ---------------------------------------------------- */
    /*  Hide QAssure widget FAB while this panel is open    */
    /* ---------------------------------------------------- */
    useEffect(() => {
        if (open) {
            document.body.setAttribute('data-issue-panel-open', 'true');
        } else {
            document.body.removeAttribute('data-issue-panel-open');
        }
        return () => document.body.removeAttribute('data-issue-panel-open');
    }, [open]);

    /* ---------------------------------------------------- */
    /*  Reset local state every time the issue changes      */
    /* ---------------------------------------------------- */
    useEffect(() => {
        if (!issue) return;
        setTitle(issue.title || '');
        setStatus(issue.status || 'Open');
        setPriority(issue.priority || 'Medium');
        setSeverity(issue.severity || 'Major');
        setModuleId(issue.module_id || '');
        setSubModuleId(issue.sub_module_id || '');
        setFeatureId(issue.feature_id || '');
        setReleaseId(issue.release_id || '');
        setAssigneeId(issue.jira_assignee_id || '');
        setLocalBugId(issue.jira_bug_id || '');
        setIsEditingTitle(false);
        setIsEditingDescription(false);
        setEditedDescription(issue.description || '');
        setEditedMediaFiles([]);
        setPreloadedMediaUrls([]);
        // Reset local media URLs (will be set after save to ensure fresh data)
        setLocalVideoUrl(null);
        setLocalScreenshotUrls(null);
        // Reset audit history state
        setShowHistory(false);
        setAuditHistory([]);
    }, [issue]);

    /* ---------------------------------------------------- */
    /*  Load comments & Slack users when issue opens        */
    /* ---------------------------------------------------- */
    useEffect(() => {
        if (!issue || !open) return;
        issueService.getComments(issue.id).then(setComments).catch(() => {});
    }, [issue, open]);

    useEffect(() => {
        api.get('/slack/users').then(r => setSlackUsers(r.data || [])).catch(() => {});
    }, []);

    /* ---------------------------------------------------- */
    /*  Pre-fetch existing media for editor                 */
    /* ---------------------------------------------------- */
    useEffect(() => {
        if (!isEditingDescription || !issue) return;
        let cancelled = false;
        const blobUrls = [];
        // Use local state values if available (for freshly saved data)
        const videoUrls = localVideoUrl ?? issue.video_url;
        const screenshotUrls = localScreenshotUrls ?? issue.screenshot_urls;
        const rawUrls = [
            // Parse multiple videos (newline-separated)
            ...(videoUrls ? videoUrls.split('\n').filter(Boolean) : []),
            ...(screenshotUrls ? screenshotUrls.split('\n').filter(Boolean) : []),
        ];
        if (rawUrls.length === 0) { setPreloadedMediaUrls([]); return; }
        Promise.all(
            rawUrls.map(async (url) => {
                try {
                    const proxyUrl = `/issues/${issue.id}/media-proxy?url=${encodeURIComponent(url)}`;
                    const res = await api.get(proxyUrl, { responseType: 'blob', timeout: 15000 });
                    const blobUrl = URL.createObjectURL(res.data);
                    blobUrls.push(blobUrl);
                    return blobUrl;
                } catch { return null; }
            })
        ).then(results => {
            if (!cancelled) setPreloadedMediaUrls(results.filter(Boolean));
        });
        return () => { cancelled = true; blobUrls.forEach(u => URL.revokeObjectURL(u)); };
    }, [isEditingDescription, issue, localVideoUrl, localScreenshotUrls]);

    /* ---------------------------------------------------- */
    /*  Generic patch helper                                */
    /* ---------------------------------------------------- */
    const patchField = useCallback(async (updates) => {
        if (!issue) return;
        setUpdating(true);
        try {
            await issueService.update(issue.id, updates);
            if (onIssueUpdated) onIssueUpdated(updates);
            setSnackbar({ open: true, message: 'Updated successfully', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Update failed: ' + (err.response?.data?.detail || err.message), severity: 'error' });
            throw err;                // let callers revert
        } finally {
            setUpdating(false);
        }
    }, [issue, onIssueUpdated]);

    /* ---------------------------------------------------- */
    /*  Audit History handler                               */
    /* ---------------------------------------------------- */
    const handleToggleHistory = async () => {
        if (showHistory) {
            setShowHistory(false);
            return;
        }
        if (auditHistory.length > 0) {
            setShowHistory(true);
            return;
        }
        // Load audit history
        if (!issue) return;
        setLoadingHistory(true);
        try {
            const history = await issueService.getAuditHistory(issue.id);
            setAuditHistory(history);
            setShowHistory(true);
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to load history', severity: 'error' });
        } finally {
            setLoadingHistory(false);
        }
    };

    /* ---------------------------------------------------- */
    /*  Field change handlers                               */
    /* ---------------------------------------------------- */
    const handleTitleSave = async () => {
        const trimmed = title.trim();
        if (!trimmed || trimmed === issue.title) { setIsEditingTitle(false); return; }
        try {
            await patchField({ title: trimmed });
            setIsEditingTitle(false);
        } catch { setTitle(issue.title); setIsEditingTitle(false); }
    };

    const handleStatusChange = async (val) => {
        setStatusAnchor(null);
        const old = status;
        setStatus(val);
        try { await patchField({ status: val }); }
        catch { setStatus(old); }
    };

    const handlePriorityChange = async (val) => {
        setPriorityAnchor(null);
        const old = priority;
        setPriority(val);
        try { await patchField({ priority: val }); }
        catch { setPriority(old); }
    };

    const handleSeverityChange = async (val) => {
        setSeverityAnchor(null);
        const old = severity;
        setSeverity(val);
        try { await patchField({ severity: val }); }
        catch { setSeverity(old); }
    };

    const handleModuleChange = async (val) => {
        setModuleAnchor(null);
        const oldM = moduleId, oldS = subModuleId, oldF = featureId;
        setModuleId(val); setSubModuleId(''); setFeatureId('');
        try { await patchField({ module_id: val || null, sub_module_id: null, feature_id: null }); }
        catch { setModuleId(oldM); setSubModuleId(oldS); setFeatureId(oldF); }
    };

    const handleSubModuleChange = async (val) => {
        setSubModuleAnchor(null);
        const oldS = subModuleId, oldF = featureId;
        setSubModuleId(val); setFeatureId('');
        try { await patchField({ sub_module_id: val || null, feature_id: null }); }
        catch { setSubModuleId(oldS); setFeatureId(oldF); }
    };

    const handleFeatureChange = async (val) => {
        setFeatureAnchor(null);
        const old = featureId;
        setFeatureId(val);
        try { await patchField({ feature_id: val || null }); }
        catch { setFeatureId(old); }
    };

    const handleReleaseChange = async (val) => {
        setReleaseAnchor(null);
        const old = releaseId;
        setReleaseId(val);
        try { await patchField({ release_id: val || null }); }
        catch { setReleaseId(old); }
    };

    const handleAssigneeChange = async (newValue) => {
        setAssigneeAnchor(null);
        const newId = newValue ? newValue.accountId : '';
        const old = assigneeId;
        setAssigneeId(newId);
        try {
            await patchField({
                jira_assignee_id: newId || null,
                jira_assignee_name: newValue?.displayName || null,
                jira_assignee_email: newValue?.emailAddress || null,
            });
        } catch { setAssigneeId(old); }
    };

    /* ---------------------------------------------------- */
    /*  Description save                                    */
    /* ---------------------------------------------------- */
    const handleSaveDescription = async () => {
        if (!issue) return;
        setIsSavingDescription(true);
        try {
            let freshIssue = null;
            if (editedMediaFiles.length > 0) {
                const formData = new FormData();
                editedMediaFiles.forEach(f => formData.append('files', f));
                const uploadResult = await issueService.uploadMedia(issue.id, formData);
                freshIssue = await issueService.update(issue.id, {
                    description: editedDescription,
                    video_url: uploadResult.video_url || issue.video_url,
                    screenshot_urls: uploadResult.screenshot_urls || issue.screenshot_urls,
                });
            } else {
                freshIssue = await issueService.update(issue.id, { description: editedDescription });
            }
            
            // Store fresh media URLs in local state so view mode renders correctly
            // This is necessary because the parent's state update is async
            if (freshIssue) {
                setLocalVideoUrl(freshIssue.video_url || null);
                setLocalScreenshotUrls(freshIssue.screenshot_urls || null);
                setEditedDescription(freshIssue.description || '');
            }
            
            setEditedMediaFiles([]);
            // Trigger parent refresh
            if (onIssueUpdated) onIssueUpdated({ description: freshIssue ? freshIssue.description : editedDescription });
            
            // Switch to view mode immediately - we have fresh data in local state
            setIsEditingDescription(false);
            setSnackbar({ open: true, message: 'Description saved', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to save description', severity: 'error' });
        } finally {
            setIsSavingDescription(false);
        }
    };

    const handleCancelEditDescription = () => {
        setEditedDescription(issue?.description || '');
        setEditedMediaFiles([]);
        setIsEditingDescription(false);
    };

    /* ---------------------------------------------------- */
    /*  Link to Story                                       */
    /* ---------------------------------------------------- */
    const handleOpenLinkDialog = async () => {
        try {
            const response = await api.get('/jira-stories/');
            setStories(response.data);
            setLinkDialogOpen(true);
        } catch { setSnackbar({ open: true, message: 'Failed to load stories', severity: 'error' }); }
    };

    const handleLinkStory = async () => {
        if (!selectedStory || !issue) return;
        try {
            await patchField({ jira_story_id: selectedStory });
            setLinkDialogOpen(false);
            setSelectedStory(null);
        } catch { /* snackbar handled in patchField */ }
    };

    /* ---------------------------------------------------- */
    /*  Delete                                              */
    /* ---------------------------------------------------- */
    const handleDelete = async () => {
        if (!issue) return;
        if (!window.confirm('Are you sure you want to delete this issue?')) return;
        try {
            await issueService.delete(issue.id);
            onClose();
            if (onIssueUpdated) onIssueUpdated();
            setSnackbar({ open: true, message: 'Issue deleted', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Delete failed', severity: 'error' });
        }
    };

    /* Create bug success */
    const handleCreateBugSuccess = (response) => {
        setLocalBugId(response.jira_bug_id);
        if (onIssueUpdated) onIssueUpdated({ jira_bug_id: response.jira_bug_id });
    };

    /* ---------------------------------------------------- */
    /*  Comment handlers                                    */
    /* ---------------------------------------------------- */
    const handlePostComment = async () => {
        if ((!commentText.trim() && !commentImageFile) || !issue) return;
        setPostingComment(true);
        try {
            let imageUrl = null;
            // Upload image first if one is attached
            if (commentImageFile) {
                const uploadResult = await issueService.uploadCommentImage(issue.id, commentImageFile);
                imageUrl = uploadResult.image_url;
            }
            const newComment = await issueService.addComment(issue.id, {
                content: commentText.trim() || (imageUrl ? '📎 Image attached' : ''),
                mentions: pendingMentions.length > 0 ? pendingMentions : null,
                image_url: imageUrl,
            });
            setComments(prev => [...prev, newComment]);
            setCommentText('');
            setPendingMentions([]);
            setCommentImageFile(null);
            if (commentImagePreview) {
                URL.revokeObjectURL(commentImagePreview);
                setCommentImagePreview(null);
            }
            setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to post comment', severity: 'error' });
        } finally {
            setPostingComment(false);
        }
    };

    const handleDeleteComment = async (commentId) => {
        try {
            await issueService.deleteComment(issue.id, commentId);
            setComments(prev => prev.filter(c => c.id !== commentId));
        } catch {
            setSnackbar({ open: true, message: 'Failed to delete comment', severity: 'error' });
        }
    };

    const handleCommentKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handlePostComment();
        }
        if (e.key === '@' || (e.key === '@' && e.shiftKey)) {
            // Will be handled by onChange detecting '@'
        }
    };

    const handleCommentPaste = (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.type.startsWith('image/')) {
                e.preventDefault();
                const file = item.getAsFile();
                if (file) {
                    // Revoke previous preview URL if exists
                    if (commentImagePreview) {
                        URL.revokeObjectURL(commentImagePreview);
                    }
                    setCommentImageFile(file);
                    setCommentImagePreview(URL.createObjectURL(file));
                }
                return;
            }
        }
    };

    const handleCommentChange = (e) => {
        const val = e.target.value;
        setCommentText(val);
        // Detect @mention trigger
        const cursorPos = e.target.selectionStart;
        const textBeforeCursor = val.substring(0, cursorPos);
        const atMatch = textBeforeCursor.match(/@(\w*)$/);
        if (atMatch) {
            setMentionFilter(atMatch[1].toLowerCase());
            setMentionAnchor(e.target);
        } else {
            setMentionAnchor(null);
            setMentionFilter('');
        }
    };

    const handleSelectMention = (user) => {
        const displayName = user.real_name || user.display_name || user.name;
        // Replace the @partial with @DisplayName
        const cursorPos = commentInputRef.current?.selectionStart || commentText.length;
        const textBefore = commentText.substring(0, cursorPos);
        const textAfter = commentText.substring(cursorPos);
        const replaced = textBefore.replace(/@(\w*)$/, `@${displayName} `);
        setCommentText(replaced + textAfter);
        setMentionAnchor(null);
        setMentionFilter('');
        setPendingMentions(prev => {
            if (prev.some(m => m.slack_id === user.id)) return prev;
            return [...prev, { slack_id: user.id, display_name: displayName }];
        });
        setTimeout(() => commentInputRef.current?.focus(), 50);
    };

    const filteredSlackUsers = slackUsers.filter(u => {
        if (!mentionFilter) return true;
        const name = (u.real_name || u.display_name || u.name || '').toLowerCase();
        return name.includes(mentionFilter);
    }).slice(0, 8);

    /* ---------------------------------------------------- */
    /*  Lookup helpers                                      */
    /* ---------------------------------------------------- */
    const moduleName  = modules.find(m => m.id === moduleId)?.name  || 'None';
    const subModuleName = subModules.find(s => s.id === subModuleId)?.name || 'None';
    const featureName = features.find(f => f.id === featureId)?.name || 'None';
    const releaseName = releases.find(r => r.id === releaseId)?.version || 'None';
    const assigneeName = issue?.jira_assignee_name ||
        (assigneeId && jiraUsersMap[assigneeId]?.displayName) || 'Unassigned';

    const filteredSubModules = subModules.filter(s => s.module_id === moduleId);
    const filteredFeatures   = features.filter(f => f.sub_module_id === subModuleId);

    // Parse screenshots from issue for viewer - prefer local state for freshly saved data
    const effectiveScreenshotUrls = localScreenshotUrls ?? issue?.screenshot_urls;
    const screenshots = effectiveScreenshotUrls ? effectiveScreenshotUrls.split('\n').filter(u => u.trim() !== '') : [];
    
    // Parse video URLs - support multiple videos (newline-separated)
    const effectiveVideoUrls = localVideoUrl ?? issue?.video_url;
    const videos = effectiveVideoUrls ? effectiveVideoUrls.split('\n').filter(u => u.trim() !== '') : [];
    
    // DEBUG: Log video URL resolution
    console.log('[IssueDetailPanel] Video URL debug:', {
        localVideoUrl,
        issueVideoUrl: issue?.video_url,
        effectiveVideoUrls,
        videoCount: videos.length,
        issueId: issue?.id
    });

    if (!issue) return null;

    /* ================================================== */
    /*  RENDER                                            */
    /* ================================================== */
    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                variant="temporary"
                hideBackdrop={true}
                disableScrollLock={true}
                ModalProps={{
                    keepMounted: true,
                    sx: { pointerEvents: 'none' },
                }}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 700, md: 850 },
                        boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
                        position: 'fixed',
                        top: 64,
                        height: 'calc(100% - 64px)',
                        pointerEvents: 'auto',
                    },
                }}
            >
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* ====== HEADER ====== */}
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box display="flex" alignItems="center" gap={1}>
                                <Chip
                                    label={`#${issue.id}`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontWeight: 700 }}
                                />
                                {issue.jira_story_id && (
                                    <Chip
                                        label={issue.jira_story_id}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        component="a"
                                        href={`https://centime.atlassian.net/browse/${issue.jira_story_id}`}
                                        target="_blank"
                                        clickable
                                    />
                                )}
                                {localBugId && (
                                    <Chip
                                        label={localBugId}
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        component="a"
                                        href={`https://centime.atlassian.net/browse/${localBugId}`}
                                        target="_blank"
                                        clickable
                                        icon={<BugIcon sx={{ fontSize: 14 }} />}
                                    />
                                )}
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                                {/* Action buttons in header */}
                                <Tooltip title="Send Slack Message">
                                    <IconButton size="small" onClick={() => setSlackDialogOpen(true)}>
                                        <img src="/icons/slack-plane.svg" alt="Slack" style={{ width: 20, height: 20 }} />
                                    </IconButton>
                                </Tooltip>
                                {!localBugId && jiraConnected && (
                                    <Tooltip title="Create Bug in JIRA">
                                        <IconButton size="small" color="error" onClick={() => setCreateBugDialogOpen(true)}>
                                            <BugIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Tooltip title="Link to Story">
                                    <IconButton size="small" color="primary" onClick={handleOpenLinkDialog}>
                                        <LinkIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Issue">
                                    <IconButton size="small" color="error" onClick={handleDelete}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        {/* TITLE – click to edit */}
                        {isEditingTitle ? (
                            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleTitleSave();
                                        if (e.key === 'Escape') { setTitle(issue.title); setIsEditingTitle(false); }
                                    }}
                                    size="small"
                                    fullWidth
                                    autoFocus
                                    variant="outlined"
                                    sx={{ '& .MuiInputBase-input': { fontSize: '1.15rem', fontWeight: 700 } }}
                                />
                                <IconButton size="small" onClick={handleTitleSave} color="primary"><SaveIcon fontSize="small" /></IconButton>
                                <IconButton size="small" onClick={() => { setTitle(issue.title); setIsEditingTitle(false); }}><CancelIcon fontSize="small" /></IconButton>
                            </Box>
                        ) : (
                            <Typography
                                variant="h6"
                                sx={{
                                    mt: 1, fontWeight: 700, lineHeight: 1.3,
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'action.hover', borderRadius: 1 },
                                    px: 0.5, py: 0.25,
                                }}
                                onClick={() => setIsEditingTitle(true)}
                                title="Click to edit title"
                            >
                                {title}
                            </Typography>
                        )}
                    </Box>

                    {/* ====== SCROLLABLE BODY ====== */}
                    <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                        {/* Metadata chips row */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
                            {/* STATUS */}
                            <Tooltip title="Click to change status">
                                <Chip
                                    label={status}
                                    size="small"
                                    sx={{ fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', ...(statusColors[status] || { bgcolor: '#757575', color: '#fff' }) }}
                                    onClick={(e) => setStatusAnchor(e.currentTarget)}
                                />
                            </Tooltip>
                            <Menu anchorEl={statusAnchor} open={Boolean(statusAnchor)} onClose={() => setStatusAnchor(null)}>
                                {['Open', 'In Development', 'Resolved', 'Fix Deployed', 'Testing in progress', 'Fix not working', 'Closed', 'Backlog', 'Defaulter'].map(s => (
                                    <MenuItem key={s} selected={s === status} onClick={() => handleStatusChange(s)}>
                                        <Chip label={s} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem', ...(statusColors[s] || {}) }} />
                                    </MenuItem>
                                ))}
                            </Menu>

                            {/* PRIORITY */}
                            <Tooltip title="Click to change priority">
                                <Chip
                                    label={priority}
                                    size="small"
                                    sx={{ fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', ...(priorityColors[priority] || { bgcolor: '#757575', color: '#fff' }) }}
                                    onClick={(e) => setPriorityAnchor(e.currentTarget)}
                                />
                            </Tooltip>
                            <Menu anchorEl={priorityAnchor} open={Boolean(priorityAnchor)} onClose={() => setPriorityAnchor(null)}>
                                {['Critical', 'High', 'Medium', 'Low'].map(p => (
                                    <MenuItem key={p} selected={p === priority} onClick={() => handlePriorityChange(p)}>
                                        <Chip label={p} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem', ...(priorityColors[p] || {}) }} />
                                    </MenuItem>
                                ))}
                            </Menu>

                            {/* SEVERITY */}
                            <Tooltip title="Click to change severity">
                                <Chip
                                    label={`Sev: ${severity}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer', ...(severityColors[severity] || {}) }}
                                    onClick={(e) => setSeverityAnchor(e.currentTarget)}
                                />
                            </Tooltip>
                            <Menu anchorEl={severityAnchor} open={Boolean(severityAnchor)} onClose={() => setSeverityAnchor(null)}>
                                {['Blocker', 'Critical', 'Major', 'Minor', 'Trivial'].map(s => (
                                    <MenuItem key={s} selected={s === severity} onClick={() => handleSeverityChange(s)}>
                                        <ListItemText primary={s} />
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>

                        {/* Second row: Module / SubModule / Feature / Release / Assignee / Reporter */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
                            {/* MODULE */}
                            <Tooltip title="Click to change module">
                                <Chip
                                    label={`Module: ${moduleName}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                                    onClick={(e) => setModuleAnchor(e.currentTarget)}
                                />
                            </Tooltip>
                            <Menu anchorEl={moduleAnchor} open={Boolean(moduleAnchor)} onClose={() => setModuleAnchor(null)} PaperProps={{ sx: { maxHeight: 300 } }}>
                                <MenuItem onClick={() => handleModuleChange('')}><em>None</em></MenuItem>
                                {modules.map(m => (
                                    <MenuItem key={m.id} selected={m.id === moduleId} onClick={() => handleModuleChange(m.id)}>
                                        {m.name}
                                    </MenuItem>
                                ))}
                            </Menu>

                            {/* SUB MODULE */}
                            <Tooltip title={moduleId ? 'Click to change sub-module' : 'Select a module first'}>
                                <span>
                                    <Chip
                                        label={`Sub: ${subModuleName}`}
                                        size="small"
                                        variant="outlined"
                                        disabled={!moduleId}
                                        sx={{ cursor: moduleId ? 'pointer' : 'default', '&:hover': moduleId ? { bgcolor: 'action.hover' } : {} }}
                                        onClick={(e) => moduleId && setSubModuleAnchor(e.currentTarget)}
                                    />
                                </span>
                            </Tooltip>
                            <Menu anchorEl={subModuleAnchor} open={Boolean(subModuleAnchor)} onClose={() => setSubModuleAnchor(null)} PaperProps={{ sx: { maxHeight: 300 } }}>
                                <MenuItem onClick={() => handleSubModuleChange('')}><em>None</em></MenuItem>
                                {filteredSubModules.map(s => (
                                    <MenuItem key={s.id} selected={s.id === subModuleId} onClick={() => handleSubModuleChange(s.id)}>
                                        {s.name}
                                    </MenuItem>
                                ))}
                            </Menu>

                            {/* FEATURE */}
                            <Tooltip title={subModuleId ? 'Click to change feature' : 'Select a sub-module first'}>
                                <span>
                                    <Chip
                                        label={`Feature: ${featureName}`}
                                        size="small"
                                        variant="outlined"
                                        disabled={!subModuleId}
                                        sx={{ cursor: subModuleId ? 'pointer' : 'default', '&:hover': subModuleId ? { bgcolor: 'action.hover' } : {} }}
                                        onClick={(e) => subModuleId && setFeatureAnchor(e.currentTarget)}
                                    />
                                </span>
                            </Tooltip>
                            <Menu anchorEl={featureAnchor} open={Boolean(featureAnchor)} onClose={() => setFeatureAnchor(null)} PaperProps={{ sx: { maxHeight: 300 } }}>
                                <MenuItem onClick={() => handleFeatureChange('')}><em>None</em></MenuItem>
                                {filteredFeatures.map(f => (
                                    <MenuItem key={f.id} selected={f.id === featureId} onClick={() => handleFeatureChange(f.id)}>
                                        {f.name}
                                    </MenuItem>
                                ))}
                            </Menu>

                            {/* RELEASE */}
                            <Tooltip title="Click to change release">
                                <Chip
                                    label={`Release: ${releaseName}`}
                                    size="small"
                                    variant="outlined"
                                    color="info"
                                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                                    onClick={(e) => setReleaseAnchor(e.currentTarget)}
                                />
                            </Tooltip>
                            <Menu anchorEl={releaseAnchor} open={Boolean(releaseAnchor)} onClose={() => setReleaseAnchor(null)} PaperProps={{ sx: { maxHeight: 300 } }}>
                                <MenuItem onClick={() => handleReleaseChange('')}><em>None</em></MenuItem>
                                {releases.map(r => (
                                    <MenuItem key={r.id} selected={r.id === releaseId} onClick={() => handleReleaseChange(r.id)}>
                                        {r.version}{r.name ? ` — ${r.name}` : ''}{r.overall_status === 'in_progress' ? ' (Active)' : ''}
                                    </MenuItem>
                                ))}
                            </Menu>

                            {/* ASSIGNEE */}
                            <Tooltip title="Click to change assignee">
                                <Chip
                                    label={assigneeName}
                                    size="small"
                                    variant="outlined"
                                    avatar={<Avatar sx={{ width: 20, height: 20, fontSize: '0.7rem' }}>{assigneeName[0]}</Avatar>}
                                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                                    onClick={(e) => setAssigneeAnchor(e.currentTarget)}
                                />
                            </Tooltip>
                            <Menu
                                anchorEl={assigneeAnchor}
                                open={Boolean(assigneeAnchor)}
                                onClose={() => setAssigneeAnchor(null)}
                                PaperProps={{ sx: { width: 300, maxHeight: 350 } }}
                            >
                                <MenuItem onClick={() => handleAssigneeChange(null)}>
                                    <ListItemText primary="Unassigned" />
                                </MenuItem>
                                <Divider />
                                {jiraUsers.map(user => (
                                    <MenuItem key={user.accountId} selected={user.accountId === assigneeId} onClick={() => handleAssigneeChange(user)}>
                                        <Avatar sx={{ width: 24, height: 24, mr: 1, fontSize: '0.7rem' }}>{user.displayName?.[0]}</Avatar>
                                        <ListItemText primary={user.displayName} secondary={user.emailAddress} />
                                    </MenuItem>
                                ))}
                            </Menu>

                            {/* REPORTER (read-only) */}
                            <Chip
                                label={`Reporter: ${issue.reporter_name || '-'}`}
                                size="small"
                                variant="outlined"
                                sx={{ opacity: 0.8 }}
                            />
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {/* ====== DESCRIPTION section ====== */}
                        <Box sx={{ mb: 2 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="subtitle1" fontWeight={600}>Description</Typography>
                                {!isEditingDescription && (
                                    <Tooltip title="Edit description">
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => {
                                                setEditedDescription(issue.description || '');
                                                setIsEditingDescription(true);
                                            }}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                            {isEditingDescription ? (
                                <Box>
                                    <RichTextEditor
                                        key={`panel-editor-${issue.id}-${preloadedMediaUrls.length}`}
                                        value={editedDescription}
                                        onChange={(html, mediaFiles) => {
                                            setEditedDescription(html);
                                            setEditedMediaFiles(mediaFiles);
                                        }}
                                        placeholder="Edit issue description..."
                                        existingMedia={preloadedMediaUrls}
                                    />
                                    <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
                                        <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancelEditDescription} disabled={isSavingDescription}>
                                            Cancel
                                        </Button>
                                        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveDescription} disabled={isSavingDescription}>
                                            {isSavingDescription ? 'Saving...' : 'Save'}
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <IssueContentRenderer
                                    htmlContent={issue.description}
                                    videoUrl={videos.map(url =>
                                        `/issues/${issue.id}/media-proxy?url=${encodeURIComponent(url)}`
                                    ).join('\n')}
                                    screenshotUrls={screenshots.map(url =>
                                        `/issues/${issue.id}/media-proxy?url=${encodeURIComponent(url)}`
                                    ).join('\n')}
                                />
                            )}
                        </Box>

                        {/* Timestamps */}
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                Created: {formatUtcToLocal(issue.created_at)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Updated: {formatUtcToLocal(issue.updated_at)}
                            </Typography>
                        </Box>

                        {/* ====== AUDIT HISTORY section ====== */}
                        <Box sx={{ mb: 2 }}>
                            <Button
                                size="small"
                                startIcon={loadingHistory ? <CircularProgress size={14} /> : <HistoryIcon fontSize="small" />}
                                endIcon={showHistory ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                onClick={handleToggleHistory}
                                disabled={loadingHistory}
                                sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.8rem' }}
                            >
                                {showHistory ? 'Hide History' : 'Show History'}
                            </Button>
                            
                            {showHistory && (
                                <Box sx={{ mt: 1.5, pl: 1, borderLeft: '2px solid #e0e0e0' }}>
                                    {auditHistory.length === 0 ? (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                            No changes recorded yet.
                                        </Typography>
                                    ) : (
                                        auditHistory.map((log) => {
                                            const userName = log.user?.full_name || log.user?.email || 'Unknown';
                                            const fieldLabel = {
                                                'status': 'Status',
                                                'priority': 'Priority',
                                                'jira_assignee_id': 'Assignee',
                                            }[log.field_name] || log.field_name;
                                            return (
                                                <Box key={log.id} sx={{ mb: 1.5, fontSize: '0.8rem' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Avatar sx={{ width: 18, height: 18, fontSize: '0.65rem', bgcolor: 'grey.400' }}>
                                                            {userName.charAt(0).toUpperCase()}
                                                        </Avatar>
                                                        <Typography variant="caption" fontWeight={600}>
                                                            {userName}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            changed <strong>{fieldLabel}</strong>
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 3, mt: 0.25 }}>
                                                        <Chip
                                                            label={log.old_value || 'None'}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ fontSize: '0.7rem', height: 20, textDecoration: 'line-through', opacity: 0.7 }}
                                                        />
                                                        <Typography variant="caption" color="text.secondary">→</Typography>
                                                        <Chip
                                                            label={log.new_value || 'None'}
                                                            size="small"
                                                            sx={{
                                                                fontSize: '0.7rem',
                                                                height: 20,
                                                                ...((log.field_name === 'status' && statusColors[log.new_value]) || {}),
                                                                ...((log.field_name === 'priority' && priorityColors[log.new_value]) || {}),
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 3, display: 'block', mt: 0.25 }}>
                                                        {formatUtcToLocal(log.created_at, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                    </Typography>
                                                </Box>
                                            );
                                        })
                                    )}
                                </Box>
                            )}
                        </Box>

                        {/* ====== COMMENTS section ====== */}
                        <Divider sx={{ mb: 2 }} />
                        <Box>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>Comments</Typography>

                            {/* Comment list */}
                            <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
                                {comments.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 1 }}>
                                        No comments yet. Be the first to comment.
                                    </Typography>
                                )}
                                {comments.map((c) => {
                                    const author = c.user?.full_name || c.user?.email || 'User';
                                    const initials = author.charAt(0).toUpperCase();
                                    const isOwn = c.user_id === (issue.created_by);
                                    return (
                                        <Box key={c.id} sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'flex-start' }}>
                                            <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: isOwn ? 'primary.main' : 'grey.500', mt: 0.5 }}>
                                                {initials}
                                            </Avatar>
                                            <Box sx={{ flex: 1, bgcolor: 'grey.50', borderRadius: 1.5, px: 1.5, py: 1, position: 'relative' }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                                                    <Typography variant="caption" fontWeight={600}>{author}</Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatUtcToLocal(c.created_at, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                        </Typography>
                                                        <IconButton size="small" sx={{ opacity: 0.4, '&:hover': { opacity: 1 } }} onClick={() => handleDeleteComment(c.id)}>
                                                            <DeleteIcon sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                    {c.content}
                                                </Typography>
                                                {c.image_url && (
                                                    <Box
                                                        sx={{
                                                            mt: 1,
                                                            cursor: 'pointer',
                                                        }}
                                                        onClick={() => setExpandedCommentImage(`/issues/${issue.id}/media-proxy?url=${encodeURIComponent(c.image_url)}`)}
                                                    >
                                                        <AuthenticatedImage
                                                            src={`/issues/${issue.id}/media-proxy?url=${encodeURIComponent(c.image_url)}`}
                                                            alt="Comment attachment"
                                                            sx={{
                                                                maxWidth: '100%',
                                                                maxHeight: 200,
                                                                borderRadius: 1,
                                                                boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                                                            }}
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })}
                                <div ref={commentsEndRef} />
                            </Box>

                            {/* Comment input with @mention */}
                            <Box>
                                {/* Image preview */}
                                {commentImagePreview && (
                                    <Box sx={{ position: 'relative', display: 'inline-block', mb: 1 }}>
                                        <Box
                                            component="img"
                                            src={commentImagePreview}
                                            alt="Preview"
                                            sx={{ maxHeight: 100, maxWidth: 200, borderRadius: 1, border: '1px solid #e0e0e0' }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setCommentImageFile(null);
                                                URL.revokeObjectURL(commentImagePreview);
                                                setCommentImagePreview(null);
                                            }}
                                            sx={{
                                                position: 'absolute', top: -8, right: -8,
                                                bgcolor: 'rgba(255,255,255,0.9)', boxShadow: 1,
                                                '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                                            }}
                                        >
                                            <CloseIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                                    <input
                                        ref={commentImageInputRef}
                                        type="file"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                setCommentImageFile(file);
                                                setCommentImagePreview(URL.createObjectURL(file));
                                            }
                                            e.target.value = '';
                                        }}
                                    />
                                    <Tooltip title="Attach screenshot">
                                        <IconButton
                                            size="small"
                                            color="default"
                                            onClick={() => commentImageInputRef.current?.click()}
                                            sx={{ mb: 0.5 }}
                                        >
                                            <ImageIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <TextField
                                        inputRef={commentInputRef}
                                        value={commentText}
                                        onChange={handleCommentChange}
                                        onKeyDown={handleCommentKeyDown}
                                        onPaste={handleCommentPaste}
                                        placeholder="Add a comment... (type @ to mention, paste screenshot)"
                                        multiline
                                        maxRows={4}
                                        size="small"
                                        fullWidth
                                        sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                                    />
                                    <IconButton
                                        color="primary"
                                        onClick={handlePostComment}
                                        disabled={(!commentText.trim() && !commentImageFile) || postingComment}
                                        sx={{ mb: 0.5 }}
                                    >
                                        {postingComment ? <CircularProgress size={20} /> : <SendIcon />}
                                    </IconButton>
                                </Box>
                            </Box>

                            {/* @Mention dropdown – using Popper so focus stays on the input */}
                            <Popper
                                open={Boolean(mentionAnchor) && filteredSlackUsers.length > 0}
                                anchorEl={mentionAnchor}
                                placement="top-start"
                                style={{ zIndex: 1500 }}
                            >
                                <ClickAwayListener onClickAway={() => { setMentionAnchor(null); setMentionFilter(''); }}>
                                    <Paper elevation={8} sx={{ maxHeight: 250, width: 280, overflow: 'auto' }}>
                                        <MenuList dense autoFocusItem={false}>
                                            {filteredSlackUsers.map(u => (
                                                <MenuItem key={u.id} onMouseDown={(e) => { e.preventDefault(); handleSelectMention(u); }} dense>
                                                    <Avatar
                                                        src={u.avatar}
                                                        sx={{ width: 24, height: 24, mr: 1, fontSize: '0.7rem' }}
                                                    >
                                                        {(u.real_name || u.name || '?')[0]}
                                                    </Avatar>
                                                    <ListItemText
                                                        primary={u.real_name || u.display_name || u.name}
                                                        secondary={u.email || ''}
                                                        primaryTypographyProps={{ fontSize: '0.85rem' }}
                                                        secondaryTypographyProps={{ fontSize: '0.7rem' }}
                                                    />
                                                </MenuItem>
                                            ))}
                                        </MenuList>
                                    </Paper>
                                </ClickAwayListener>
                            </Popper>

                            {/* Show pending mentions as chips */}
                            {pendingMentions.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                    {pendingMentions.map(m => (
                                        <Chip
                                            key={m.slack_id}
                                            label={`@${m.display_name}`}
                                            size="small"
                                            color="info"
                                            variant="outlined"
                                            onDelete={() => setPendingMentions(prev => prev.filter(p => p.slack_id !== m.slack_id))}
                                            sx={{ fontSize: '0.75rem' }}
                                        />
                                    ))}
                                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 0.5 }}>
                                        will be notified via Slack
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Drawer>

            {/* ====== DIALOGS (rendered outside drawer) ====== */}
            <SlackMessageDialog
                open={slackDialogOpen}
                onClose={() => setSlackDialogOpen(false)}
                ticket={{
                    key: issue.jira_story_id || `ISSUE-${issue.id}`,
                    summary: issue.title,
                    priority: issue.priority,
                    status: issue.status,
                    assignee: issue.jira_assignee_name || 'Unassigned',
                }}
                defaultRecipientEmail={issue.jira_assignee_email}
                contextType="issue"
                issueId={issue.id}
                releaseId={issue.release_id}
            />

            <CreateJiraBugDialog
                open={createBugDialogOpen}
                onClose={() => setCreateBugDialogOpen(false)}
                issue={issue}
                onSuccess={handleCreateBugSuccess}
            />

            {/* Link to Story Dialog */}
            {linkDialogOpen && (
                <Box
                    sx={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        bgcolor: 'rgba(0,0,0,0.4)', zIndex: 1400,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                    onClick={() => setLinkDialogOpen(false)}
                >
                    <Box
                        sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 3, width: 500, maxWidth: '90%' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Typography variant="h6" gutterBottom>Link Issue to Story</Typography>
                        <Autocomplete
                            value={selectedStory}
                            onChange={(e, v) => setSelectedStory(v)}
                            options={stories.map(s => s.story_id)}
                            renderInput={(params) => <TextField {...params} label="Select Story" placeholder="Search..." fullWidth />}
                            renderOption={(props, option) => {
                                const story = stories.find(s => s.story_id === option);
                                return (
                                    <Box component="li" {...props}>
                                        <Box>
                                            <Typography variant="body2" fontWeight="bold">{option}</Typography>
                                            <Typography variant="caption" color="text.secondary">{story?.title}</Typography>
                                        </Box>
                                    </Box>
                                );
                            }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
                            <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
                            <Button variant="contained" disabled={!selectedStory} onClick={handleLinkStory}>Link Story</Button>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Loading overlay */}
            {updating && (
                <Box
                    sx={{
                        position: 'fixed', top: 68, right: 10, zIndex: 1400,
                    }}
                >
                    <CircularProgress size={20} />
                </Box>
            )}

            {/* Expanded Comment Image Dialog */}
            <Dialog
                open={!!expandedCommentImage}
                onClose={() => setExpandedCommentImage(null)}
                maxWidth="lg"
                PaperProps={{
                    sx: { bgcolor: 'transparent', boxShadow: 'none' }
                }}
            >
                <DialogContent sx={{ p: 0, position: 'relative' }}>
                    <IconButton
                        onClick={() => setExpandedCommentImage(null)}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                            zIndex: 1,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {expandedCommentImage && (
                        <AuthenticatedImage
                            src={expandedCommentImage}
                            alt="Expanded comment attachment"
                            sx={{
                                maxWidth: '90vw',
                                maxHeight: '90vh',
                                display: 'block',
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default IssueDetailPanel;
